import { GridFilterUiConfiguration } from '../../../types/filters/types';
import { Filter } from '../../../grid/gridFilters/types';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { ColumnDetails, ColumnType } from '../../../grid/types/common';
import { translateFn } from '../../../utils/translator.helper';
import { Column, ColumnKeys, Permissions, WacPagesGridEvents, EnumValues, StatusType, WebAccessControlCustomPagesGrid } from '../types';
import { defaultPageSizeOptions, GridColumnRenderers, GridViewMetadata } from '../../../grid/gridView/types';
import { html } from 'lit';
import moment from 'moment';
import { permissionsRenderer } from '../../maintenanceWindowGrid/view/CustomRenderers';
import { GridBaseDataProvider } from '../../../grid/GridBaseDataProvider';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { Rights } from '@nirvana/shared/constants';
import { SelectOption } from '../../../ui/gzSelect/types';
import { GridTitleConfig } from '../../../grid/uiElements/gridViewTitle/types';

export class WebAccessControlCustomPagesGridConfig {

  private gridColumns: Column[] = [
    Column.PAGE_NAME,
    Column.STATUS,
    Column.LAST_MODIFIED_DATE,
    Column.LAST_EDITED_BY,
    Column.PERMISSIONS,
    Column.IN_POLICIES,
  ];

  private predefinedFiltersKeys: Array<ColumnKeys> = [
    ColumnKeys.PAGE_NAME,
    ColumnKeys.STATUS,
    ColumnKeys.LAST_MODIFIED_DATE,
    ColumnKeys.LAST_EDITED_BY,
    ColumnKeys.PERMISSIONS,
  ];

  private filtersUiConfig:Array<GridFilterUiConfiguration> = [
    {key: ColumnKeys.LAST_MODIFIED_DATE, config: { enableSelectAllToDate: true }},
  ];

  private gridDefaultFilters: Array<Filter<ColumnKeys>> =  [
    {[ColumnKeys.PAGE_NAME]: { contains: '' }},
    {[ColumnKeys.STATUS]: { in: [] }},
    {[ColumnKeys.LAST_MODIFIED_DATE]: { timeRange: { kind: TimeRangeKind.ALL_TO_DATE }}},
    {[ColumnKeys.LAST_EDITED_BY]: { contains: '' }},
    {[ColumnKeys.PERMISSIONS]: { in: [] }},
  ];

  private allColumnDetails: Array<ColumnDetails<Column, ColumnKeys>>  =  [
    {
      column: Column.PAGE_NAME,
      key: ColumnKeys.PAGE_NAME,
      columnName: translateFn('Name'),
      type: ColumnType.STRING,
      removable: false,
      sortable: true,
      filterResetValue: {contains: ''},
    },
    {
      column: Column.STATUS,
      key: ColumnKeys.STATUS,
      columnName: translateFn('Status'),
      type: ColumnType.ENUM,
      values: this.getEnumValues(this.getAllStatusTypeLabels),
      removable: false,
      sortable: false,
      filterResetValue: [],
    },
    {
      column: Column.LAST_MODIFIED_DATE,
      key: ColumnKeys.LAST_MODIFIED_DATE,
      columnName: translateFn('Last modified'),
      type: ColumnType.DATE,
      removable: false,
      sortable: true,
      showTimePickers: false,
      filterResetValue: {timeRange: { kind: TimeRangeKind.ALL_TO_DATE }},
    },
    {
      column: Column.LAST_EDITED_BY,
      key: ColumnKeys.LAST_EDITED_BY,
      columnName: translateFn('Last edited by'),
      type: ColumnType.STRING,
      removable: false,
      sortable: true,
      filterResetValue: {contains: ''},
    },
    {
      column: Column.PERMISSIONS,
      key: ColumnKeys.PERMISSIONS,
      columnName: translateFn('Permissions'),
      type: ColumnType.ENUM,
      values: this.getEnumValues(this.getAllPermissionsLabels),
      removable: false,
      sortable: true,
      filterResetValue: {in: [Permissions.SHARED, Permissions.NOT_SHARED]},
    },
    {
      column: Column.IN_POLICIES,
      key: ColumnKeys.IN_POLICIES,
      columnName: translateFn('In policies'),
      type: ColumnType.NUMBER,
      removable: false,
      sortable: true,
      filteringDisabled: true,
    },
  ];

  private columnRenderers: GridColumnRenderers = {
    [ColumnKeys.PAGE_NAME]: rowData => {
      return html`<gz-button class="-ml-2" lowercase type="quiet" color="primary" buttonheight="medium" buttonfontsize="small"  @click="${this.onPageNameCellClick.bind(this, rowData)}">${rowData.item.name}</gz-button>`;
    },
    [ColumnKeys.STATUS]: rowData => {
      const status = this.getAllStatusTypeLabels();
      const label = rowData.item?.inPoliciesCount >= 1 ? status[StatusType.USED] : status[StatusType.UNUSED];
      return html`<gz-grid-cell-default-renderer .label="${label}"></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.LAST_MODIFIED_DATE]: rowData => {
      return html`<gz-grid-cell-default-renderer .label="${moment(rowData.item.updated.date).format('DD MMM YYYY, HH:mm')}"></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.LAST_EDITED_BY]: rowData => {
      return html` <gz-grid-cell-default-renderer .label="${rowData.item.updated.userName}"></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.PERMISSIONS]: permissionsRenderer,
    /*
    // todo: needs to be implemented
    [ColumnKeys.IN_POLICIES]: rowData => {
      return html`<gz-button class="-ml-2" lowercase type="quiet" color="primary" buttonheight="medium" buttonfontsize="small" @click="${this.onInPoliciesCellClick.bind(this, rowData)}">${rowData.item.inPoliciesCount}</gz-button>`;
    },
    */
  };

  private inlineRowActions: Array<SelectOption> = [
    /*
     // todo: needs to be implemented
    {
      title: translateFn('Edit assignments'),
      value: WacPagesGridEvents.EDIT_ASSIGNMENTS,
      disabled: !Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE]),
    },
    */
    {
      title: translateFn('Delete'),
      value: WacPagesGridEvents.DELETE_PAGE,
      customClass: 'danger',
      disabled: !Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE]),
    },
  ];


  constructor(private readonly dataProvider:GridBaseDataProvider<Column, ColumnKeys>) {
  }

  public setGridViewMetadata(grid: WebAccessControlCustomPagesGrid) {
    Object.assign(grid, this.getGridViewMetadata());
  }

  public getGridViewMetadata(): GridViewMetadata<Column, ColumnKeys> {
    return {
      viewId: 'web-access-control-custom-pages',
      filters: this.gridDefaultFilters,
      predefinedFiltersKeys: this.predefinedFiltersKeys,
      columns: this.gridColumns,
      pageSize: 100,
      isCompactViewChecked: false,
      hasSelectableRows: true,
      hasPagination: true,
      pageSizeOptions: defaultPageSizeOptions,
      columnRenderers: this.columnRenderers,
      showGlobalActions: true,
      columnConfigs: [],
      allColumnDetails: this.allColumnDetails,
      pageNumber: 1,
      filtersUiConfigs: this.filtersUiConfig,
      dataProvider: this.dataProvider,
      enableCSVExport: false,
      sortColumnName: ColumnKeys.LAST_MODIFIED_DATE,
      sortDirection: 'desc',
      inlineRowActions: this.inlineRowActions,
      titleConfig: new GridTitleConfig(translateFn('All Web Access Control Custom Pages')),
    };
  }

  public getEnumValues(keyValueMapFn: Function): EnumValues[] {
    const keyValueMap: Record<number, string> = keyValueMapFn();
    const labels: EnumValues[] = [];

    for (const [value, label] of Object.entries(keyValueMap)) {
      labels.push({
        label: label,
        value: Number(value),
      });
    }
    return labels.sort((a, b) => a.label.localeCompare(b.label));
  }

  public getAllPermissionsLabels(): Record<Permissions, string> {
    return {
      [Permissions.NOT_SHARED]: translateFn("Not Shared"),
      [Permissions.SHARED]: translateFn("Shared"),
    };
  }

  public getAllStatusTypeLabels(): Record<StatusType | number, string> {
    return {
      [StatusType.UNUSED]: translateFn("Unused"),
      [StatusType.USED]: translateFn("Used"),
    };
  }

  public onPageNameCellClick(rowData, event: CustomEvent) {
    (event.target as GzButtonWebComponent)?.dispatchEvent(new CustomEvent(WacPagesGridEvents.CLICK_ON_PAGE_NAME, {
      bubbles: true,
      composed: true,
      detail: {
        rowData: rowData,
      },
    }));
  }

  public onInPoliciesCellClick(rowData, event: CustomEvent) {
    (event.target as GzButtonWebComponent)?.dispatchEvent(new CustomEvent(WacPagesGridEvents.CLICK_ON_IN_POLICIES, {
      bubbles: true,
      composed: true,
      detail: {
        companyId: rowData.item.company.id,
        wacPage: {
          _id: rowData.item._id,
          name: rowData.item.name,
        },
      },
    }));
  }

}
